import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useDeleteParams from '../../hooks/useDeleteParams';
import useAuth from '../../hooks/useAuth';
import { authenticationService, eventService } from '../../services';
import { PersonalEvent as Event, EventParticipationAnswer } from '../../types/event';
import { CustomFieldResponses, InviteType } from '../../types/invites';
import { getUserId } from '../../services/httpService';
import { getEventAttendees, getPollResponses } from '../../utils/eventUtils';
import { formatCustomFieldResponsesFromCustomFields } from '../../utils/formatUtils';
import { LoginPageState, SuccessPageState } from '../../types/navigation';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import EventOverview from '../../components/overviews/EventOverview';
import GenericInput from '../../components/forms/GenericInput';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import Button from '../../components/misc/Button';
import AttendanceComponent from '../../components/misc/AttendanceComponent';
import { issueEventTicketUnit } from '../../services/eventService';
import EventTicketPayment from '../../components/payment/EventTicketPayment';
import { checkInviteEmailMismatch } from '../../utils/inviteUtils';

function AuthenticatedEventRespondPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  useDeleteParams('link');
  const linkId = params.get('id');

  const [event, setEvent] = useState<Event>();
  const state = location.state as { eventId: number } | undefined;
  const [selectedAnswer, setSelectedAnswer] = useState<EventParticipationAnswer | null>(null);
  const [remark, setRemark] = useState<string>('');
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>([]);

  // payment flow states
  const [ticketVeraId, setTicketVeraId] = useState<string | null>(null);
  const [showPaymentStep, setShowPaymentStep] = useState<boolean>(false);
  const [autoOpened, setAutoOpened] = useState<boolean>(false);
  const [emailMismatch, setEmailMismatch] = useState<boolean>(false);

  useEffect(() => {
    if (state?.eventId) {
      eventService
        .getEvent(state.eventId)
        .then((e) => setEvent(e))
        .catch(() => {
          toast.error(t('toast.error.general.inviteNotFound'));
          navigate('/');
        });
    } else if (linkId && !event) {
      eventService
        .getEventInviteWithToken(linkId as string)
        .then((e) => setEvent(e))
        .catch(() => {
          toast.error(t('toast.error.general.inviteNotFound'));
          navigate('/');
        });
    }
  }, [state?.eventId, linkId, navigate, t]);

  useEffect(() => {
    try {
      const userId = getUserId();
      if (!userId || !event) return;

      if (event.isDatePicker) {
        navigate(`/date-picker/invite?id=${event.id}`);
      }

      const userAnswer = event.participants.find((participant) => participant.id === userId);
      if (userAnswer) {
        setSelectedAnswer(userAnswer.answer || EventParticipationAnswer.PENDING);
        setRemark(userAnswer.description || '');
      }
      const responses = getPollResponses(userId, event!.customFields);
      setCustomFieldResponses(formatCustomFieldResponsesFromCustomFields(event!.customFields, responses));

      const emailCheck = checkInviteEmailMismatch((event as any).invitedEmail, auth?.email, InviteType.EVENT);
      setEmailMismatch(emailCheck.status === 'email-mismatch');

      if (autoOpened) return;
      const deadlineIsPassedLocal = event.deadline ? new Date() > new Date(event.deadline) : false;
      if (deadlineIsPassedLocal || !event.priceOfTicket) return;

      setAutoOpened(true);

      if (event.eventTicketUnit) {
        setTicketVeraId(event.eventTicketUnit.veraLink);
        setShowPaymentStep(true);
      } else {
        issueEventTicketUnit(event.id)
          .then((res) => {
            if (state?.eventId) {
              eventService.getEvent(state.eventId).then((e) => setEvent(e));
            } else if (linkId) {
              eventService.getEventInviteWithToken(linkId as string).then((e) => setEvent(e));
            }
            setTicketVeraId(res.veraId);
            setShowPaymentStep(true);
          })
          .catch(() => {
            toast.error(t('toast.error.general.inviteNotFound'));
          });
      }
    } catch (error) {
      console.error(error);
    }
  }, [event, autoOpened, linkId, t, auth?.email]);

  const validate = () => {
    if (!selectedAnswer || selectedAnswer === EventParticipationAnswer.PENDING) {
      toast.error(t('toast.error.field.noAnswer'));
      return false;
    }
    if (maximumAttendeesReached && selectedAnswer === EventParticipationAnswer.YES) {
      toast.error(t('toast.error.events.maximumAttendeesReached'));
      return false;
    }

    if (event!.customFields.length > 0) {
      return event!.customFields.every((field) => {
        if (field.mandatory && !customFieldResponses[field.customFieldId]) {
          toast.error(t('toast.error.field.mandatoryStar'));
          return false;
        }
        return true;
      });
    }

    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validate() || !event?.id) return;

    await eventService.respondToEvent(`${event.id}`, selectedAnswer!, remark);

    if (event!.customFields.length > 0) {
      await eventService.respondToEventPollBulk(`${event.id}`, customFieldResponses);
    }

    const successState: SuccessPageState = { translationKey: 'page.events.invite.message.success' };
    navigate('/success', { state: successState });
  };

  if (!event) return <></>;

  const handleSwitchAccount = (): void => {
    authenticationService.logOut();
    const loginState: LoginPageState = {
      inviteType: InviteType.EVENT,
      eventId: `${event.id}`,
      hideBackButton: true,
    };
    navigate('/login', { state: loginState });
  };

  const { maximumAttendees, deadline } = event;

  const numberOfAttendees = getEventAttendees(event);
  const deadlineIsPassed = deadline ? new Date() > new Date(deadline) : false;
  const maximumAttendeesReached = maximumAttendees ? numberOfAttendees >= maximumAttendees : false;
  const remarkPlaceholder = selectedAnswer
    ? `${t(`page.events.invite.placeholder.eg`)} ${t(`page.events.invite.placeholder.${selectedAnswer}.reason${Math.floor(Math.random() * 10) + 1}`)}`
    : '';

  if (emailMismatch && (event as any).invitedEmail && auth?.email) {
    return (
      <ResponsePageWrapper>
        {event && (
          <ResponsePageWrapper.SidePanel>
            <EventOverview event={event} />
          </ResponsePageWrapper.SidePanel>
        )}
        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-medium">{t('page.events.invite.authWrongAccount.title')}</h2>
          <p className="text-sm">
            {t('page.events.invite.authWrongAccount.message', {
              inviteeEmail: (event as any).invitedEmail,
              currentEmail: auth.email,
            })}
          </p>
        </div>

        <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
          <Button className="underline" onClick={() => navigate('/')}>
            {t('general.cancel')}
          </Button>
          <Button variant="primary" className="px-[32px]" onClick={handleSwitchAccount}>
            {t('page.events.invite.authWrongAccount.button')}
          </Button>
        </div>
      </ResponsePageWrapper>
    );
  }

  return (
    <ResponsePageWrapper>
      {event && (
        <ResponsePageWrapper.SidePanel>
          <EventOverview event={event} />
        </ResponsePageWrapper.SidePanel>
      )}

      {!deadlineIsPassed && !event.priceOfTicket && (
        <div className="flex flex-col gap-4">
          <h2 className="mt-4 text-2xl font-semibold text-primary-900">{t('page.events.invite.areYouAttending')}</h2>
          <div className="flex gap-4">
            <AttendanceComponent
              selectedAnswer={selectedAnswer}
              setSelectedAnswer={setSelectedAnswer}
              disableYes={maximumAttendeesReached}
            />
          </div>
          <div>
            <GenericInput
              label={t('page.events.invite.note')}
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              placeholder={selectedAnswer !== EventParticipationAnswer.PENDING ? remarkPlaceholder : ''}
              textarea
            />
          </div>

          {event && event.customFields.length > 0 && (
            <>
              <div className="text-lg">{t('page.events.invite.polls.title')}</div>
              <CustomFieldsForm
                fields={event.customFields}
                response={customFieldResponses}
                setResponse={setCustomFieldResponses}
              />
            </>
          )}

          <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
            <Button variant="primary" className="px-[32px]" onClick={handleSubmit}>
              {t('general.save')}
            </Button>
          </div>
        </div>
      )}

      {!deadlineIsPassed && event.priceOfTicket && (
        <div className="flex flex-col gap-4">
          <div className="flex w-full items-end justify-between">
            {showPaymentStep && (
              <EventTicketPayment
                veraId={ticketVeraId!}
                eventId={event.id}
                onPaymentSuccess={() => {
                  setShowPaymentStep(false);
                  eventService.getEventInviteWithToken(linkId as string).then((e) => setEvent(e));
                }}
              />
            )}
          </div>
        </div>
      )}
    </ResponsePageWrapper>
  );
}

export default AuthenticatedEventRespondPage;
